/* eslint-disable @atlaskit/design-system/no-html-anchor */
/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */
/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * TODO: GRAPHYTE-2511 wire up a real destination for the "View docs"
 * button (currently has no `href` / onClick handler) and source the MCP
 * tool catalogue from configuration rather than the hardcoded list below.
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { token } from '@atlaskit/tokens';

import { TWG_BRANDING } from '../../../../../branding';
import { TWG_CLI_DOCS_URL } from '../../../../../constants';
import atlassianGenericIcon from '../../../../icons/atlassian-web-crawler.svg';
import confluenceIcon from '../../../../icons/confluence.svg';
import jiraIcon from '../../../../icons/jira.svg';
import jsmOperationsIcon from '../../../../icons/jsm_operations.svg';
import { messages } from '../messages';

/**
 * Each pill represents an MCP tool callable against the Teamwork Graph
 * server. Tool names are API identifiers and intentionally not localised.
 */
type McpTool = {
	name: string;
	icon: string;
};

const MCP_TOOLS: McpTool[] = [
	{ name: 'getTeamworkGraphContext', icon: atlassianGenericIcon },
	{ name: 'getJiraIssue', icon: jiraIcon },
	{ name: 'createConfluencePage', icon: confluenceIcon },
	{ name: 'getTeamworkGraphObject', icon: atlassianGenericIcon },
	{ name: 'updateConfluencePage', icon: confluenceIcon },
	{ name: 'getJsmOpsAlerts', icon: jsmOperationsIcon },
	{ name: 'searchAtlassian', icon: atlassianGenericIcon },
	{ name: 'getConfluencePage', icon: confluenceIcon },
	{ name: 'createJiraIssue', icon: jiraIcon },
	{ name: 'fetchAtlassian', icon: atlassianGenericIcon },
	{ name: 'getConfluenceSpaces', icon: confluenceIcon },
	{ name: 'addCommentToJiraIssue', icon: jiraIcon },
];

const styles = cssMap({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.300'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '74px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '120px',
		'@media (max-width: 48rem)': {
			paddingInline: token('space.400'),
			paddingBlock: token('space.500'),
		},
	},
	heading: {
		margin: 0,
		font: token('font.heading.large'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		textAlign: 'center',
		fontVariantNumeric: 'tabular-nums lining-nums',
	},
	pillRow: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		width: '100%',
		maxWidth: '960px',
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '6px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		margin: 0,
	},
	pillIcon: {
		width: '16px',
		height: '16px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		userSelect: 'none' as const,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		pointerEvents: 'none' as const,
	},
	pillLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '14px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '28px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#DDDEE1',
		whiteSpace: 'nowrap',
	},
	viewDocsButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#292A2E',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition:
			'background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			borderColor: '#44474C',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.12)',
			transform: 'scale(0.98)',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

export const McpTools = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.root}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/design-system/use-heading */}
			<h3 css={styles.heading}>
				<FormattedMessage {...messages.mcpToolsHeading} />
			</h3>
			<ul css={styles.pillRow}>
				{MCP_TOOLS.map((tool) => (
					<li key={tool.name} css={styles.pill}>
						<img css={styles.pillIcon} src={tool.icon} alt="" aria-hidden="true" />
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<span css={styles.pillLabel}>{tool.name}</span>
					</li>
				))}
			</ul>
			{/* eslint-disable-next-line @atlassian/a11y/anchor-is-valid */}
			<a
				aria-label={formatMessage(messages.viewDocsAriaLabel, {
					twg_branding: TWG_BRANDING,
				})}
				css={styles.viewDocsButton}
				target="_blank"
				href={TWG_CLI_DOCS_URL}
				rel="noopener noreferrer"
			>
				<FormattedMessage {...messages.viewDocs} />
				<ArrowUpRightIcon label="" color="currentColor" />
			</a>
		</div>
	);
};
